export type AiBlockType =
  | 'phoneChat'
  | 'phoneCall'
  | 'phoneMail'
  | 'phoneMailInteract'
  | 'situationSortGame';

export const AI_BLOCK_TYPE_OPTIONS: Array<{ type: AiBlockType; label: string }> = [
  { type: 'phoneChat', label: 'SMS' },
  { type: 'phoneCall', label: 'Appel' },
  { type: 'phoneMail', label: 'Mail téléphone (triage)' },
  { type: 'phoneMailInteract', label: 'Mail interactif (un seul mail)' },
  { type: 'situationSortGame', label: 'Jeu de tri' },
];

export interface AiBlockTypeGuide {
  howItWorks: string;
  situation: string;
  examples: string[];
  placeholder: string;
}

export const AI_BLOCK_GUIDES: Record<AiBlockType, AiBlockTypeGuide> = {
  phoneChat: {
    howItWorks:
      'Conversation SMS sur le téléphone du héros. Messages du contact puis choix de réponse (replyOptions) avec répliques en chaîne.',
    situation:
      'Mise en situation : échange SMS avec un collègue, la DSI ou un contact externe sur un incident cyber.',
    examples: [
      'Alerte DSI : connexion inhabituelle, le joueur confirme ou infirme',
      'Collègue qui demande un mot de passe par SMS',
      'Code 2FA reçu par erreur — que répondre ?',
    ],
    placeholder:
      'Ex. : SMS de la DSI signalant une connexion depuis l\'étranger à 3h du matin. 2 réponses possibles du joueur avec réactions du contact.',
  },

  phoneCall: {
    howItWorks:
      'Appel téléphonique entrant. Le contact parle, le joueur choisit ses répliques (replyOptions imbriqués possibles).',
    situation:
      'Mise en situation : appel urgent du ministère, d\'un client, ou d\'un escroc se faisant passer pour le support IT.',
    examples: [
      'Appel du matin : mission de dernière minute',
      'Support IT qui demande le mot de passe par téléphone (arnaque)',
      'Appel avec branches : accepter / refuser / demander des précisions',
    ],
    placeholder:
      'Ex. : Kevin appelle tôt le matin pour une mission urgente au ministère. 2 branches de dialogue avec humour léger.',
  },

  phoneMail: {
    howItWorks:
      'Boîte mail sur le téléphone avec plusieurs messages à trier. Chaque mail a des boutons (archiver, signaler…). La bonne réponse donne un bouclier PEDA. Réactions humoristiques du héros après le clic.',
    situation:
      'Mise en situation : matinée au bureau, boîte mail à nettoyer — mélange de mails légitimes et de tentatives de phishing.',
    examples: [
      '3 mails à trier sur la cybersecu : canular interne, CR de réunion, phishing mot de passe',
      '5 mails dont 2 pièges (domaine suspect, urgence artificielle)',
      'Mails ministère avec fautes dans l\'expéditeur et liens douteux',
    ],
    placeholder:
      'Ex. : 3 emails à trier sur la cybersecu. Un canular interne, un CR légitime, un phishing avec domaine « cultuure-gouv.fr ». Boutons archiver/signaler, boucliers sur les bonnes réponses.',
  },

  phoneMailInteract: {
    howItWorks:
      'Un seul mail que le joueur ouvre et lit (pas de triage). Idéal pour une lettre de mission, un document narratif ou une info contextuelle.',
    situation:
      'Mise en situation : le héros reçoit un mail important à lire avant de continuer (mission, consignes, pièces jointes volumineuses…).',
    examples: [
      'Lettre de mission du directeur avec 11 pièces jointes',
      'Mail RH avec consignes de sécurité informatique',
      'Convocation urgente avec ton administratif',
    ],
    placeholder:
      'Ex. : Mail du directeur avec lettre de mission, accès temporaire et 11 pièces jointes. Ton formel, humour sur le volume des PJ.',
  },

  situationSortGame: {
    howItWorks:
      'Mini-jeu drag-and-drop : le joueur classe des situations narratives dans des catégories. Chaque bonne réponse donne un feedback pédagogique et un bouclier PEDA.',
    situation:
      'Mise en situation : exercice de classification cyber (signaux faibles, phishing, manipulation, exposition de données…). Souvent animé par un RSSI ou formateur.',
    examples: [
      '4 situations du matin au ministère : mail avec PJ excessives, phishing cultuure-gouv.fr, journaliste non accréditée, badge provisoire légitime. Catégories RSSI M1.',
      '5 situations sur la messagerie : 2 légitimes, 2 phishing, 1 canular interne. Consigne formateur DSI.',
      '3 situations sécurité physique : badge tailgating, porte ouverte, visiteur avec escorte — catégories Incident / Rien à signaler / Manipulation.',
    ],
    placeholder:
      'Ex. : 4 situations à classer pour un RSSI. Catégories : Information exposée, Tentative d\'intrusion, Manipulation, Rien à signaler. Thème : matinée au ministère, niveau intermédiaire, feedbacks pédagogiques avec humour léger.',
  },
};

export interface AiBlockContext {
  scenarioTitle?: string;
  chapterTitle?: string;
  characters?: Array<{ id: string; name: string }>;
  recentBlocks?: string[];
  situationSortCategories?: Array<{ id: string; label: string }>;
}

export interface GenerateBlockResponse {
  body: Record<string, unknown>;
  blockType: AiBlockType;
}
